import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles } from "lucide-react";

const formatNumber = (n) => new Intl.NumberFormat().format(n || 0);

const AnimatedNumber = ({ value, duration = 900 }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (value <= 0) { setDisplay(0); return; }
    const start = performance.now();
    const from = 0;
    let raf;
    const step = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(from + (value - from) * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <span>{formatNumber(display)}</span>;
};

/**
 * Live Human Count banner. Reads /api/public/human-count (cached, cheap).
 * Counted from both the on-site "I sent it" button (source=web) and the Discord /sent command (source=discord).
 */
export const HumanCountBanner = () => {
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/public/human-count")
      .then((r) => setTotal(r?.data?.total ?? 0))
      .catch(() => setTotal(0))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section
      data-testid="human-count-banner"
      aria-label="Human count"
      className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-10 pb-16 sm:pb-20"
    >
      <div className="relative nb-box overflow-visible px-6 sm:px-10 py-10 sm:py-12">
        {/* Tilted "Fun fact" blurb sitting inside the top-right corner of the box */}
        <div
          data-testid="human-count-fun-fact"
          aria-label="Fun fact about the letter h in Cohnect"
          className="pointer-events-none hidden sm:block"
          style={{
            position: "absolute",
            top: "1.5rem",
            right: "1.5rem",
            transform: "rotate(-6deg)",
            transformOrigin: "right top",
            whiteSpace: "nowrap",
          }}
        >
          <div className="border-2 border-[color:var(--coh-ink)] bg-[color:var(--coh-paper-2)] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-[color:var(--coh-ink)] font-mono shadow-nb-sm">
            Fun fact: the <em className="not-italic text-[color:var(--coh-blue)]">h</em> stands for human
          </div>
        </div>

        <div className="kicker inline-flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5" /> The Human Count
        </div>

        <div className="mt-4 flex items-baseline gap-3 flex-wrap">
          <div
            data-testid="human-count-total"
            className="font-display font-black text-5xl sm:text-6xl md:text-7xl text-[color:var(--coh-blue)] leading-none tracking-[-0.03em]"
          >
            {loading ? "\u2014" : <AnimatedNumber value={total || 0} />}
          </div>
          <div className="font-display font-extrabold text-2xl sm:text-3xl text-[color:var(--coh-ink)] leading-none">
            humans cohnected
          </div>
        </div>

        <p className="mt-4 text-sm sm:text-base text-[color:var(--coh-ink-muted)] max-w-2xl leading-relaxed">
          Every time someone sends a message inspired by Cohnect — from the site or from our
          Discord — this number ticks up by one.
        </p>
      </div>
    </section>
  );
};
